/**
 * Analytics Utility Functions
 *
 * Provides type-safe helper functions for PostHog event tracking.
 * Use these throughout the app instead of calling posthog directly.
 */

import posthog from './posthog'

/**
 * Track a custom event
 * @param eventName - Name of the event (use snake_case)
 * @param properties - Optional event properties
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>
) => {
  if (typeof window !== 'undefined') {
    posthog.capture(eventName, {
      ...properties,
      environment: process.env.NODE_ENV,
    })
  }
}

/**
 * Identify a user with properties
 * @param userId - Unique user identifier (use clerk_id, not email)
 * @param properties - User properties to set
 */
export const identifyUser = (
  userId: string,
  properties?: Record<string, unknown>
) => {
  if (typeof window !== 'undefined') {
    posthog.identify(userId, properties)
  }
}

/**
 * Set user properties without identifying
 * Useful for updating properties after initial identification
 * @param properties - Properties to set or update
 */
export const setUserProperties = (properties: Record<string, unknown>) => {
  if (typeof window !== 'undefined') {
    posthog.setPersonProperties(properties)
  }
}

/**
 * Reset user (call on logout)
 * Clears the user's distinct_id and all stored person properties
 */
export const resetUser = () => {
  if (typeof window !== 'undefined') {
    posthog.reset()
  }
}

/**
 * Track a page view manually
 * (Usually handled automatically by PostHogProvider, but available if needed)
 * @param url - Current page URL
 * @param properties - Optional additional properties
 */
export const trackPageView = (url?: string, properties?: Record<string, unknown>) => {
  if (typeof window !== 'undefined') {
    posthog.capture('$pageview', {
      $current_url: url || window.location.href,
      ...properties,
    })
  }
}

// =============================================================================
// Domain-Specific Analytics
// =============================================================================

/**
 * Transcript Analytics
 * Track transcript generation and tool usage events
 */
export const TranscriptAnalytics = {
  /**
   * Track when a transcript is generated
   */
  generated: (videoId: string, toolName: string) => {
    trackEvent('transcript_generated', {
      video_id: videoId,
      tool_name: toolName,
    })
  },

  /**
   * Track when a transcript is downloaded
   */
  downloaded: (videoId: string, format: string) => {
    trackEvent('transcript_downloaded', {
      video_id: videoId,
      format,
    })
  },

  /**
   * Track when a tool is used
   */
  toolUsed: (toolName: string, videoId?: string) => {
    trackEvent('tool_used', {
      tool_name: toolName,
      video_id: videoId,
    })
  },
}

/**
 * Tool Analytics
 * Track tool page views, generation events, and result interactions
 */
export const ToolAnalytics = {
  pageViewed: (toolSlug: string, toolName: string) => {
    trackEvent('tool_page_viewed', { tool_slug: toolSlug, tool_name: toolName })
  },

  generationStarted: (toolType: string, videoId: string, source: string) => {
    trackEvent('tool_generation_started', { tool_type: toolType, video_id: videoId, source })
  },

  generationCompleted: (toolType: string, videoId: string, generationTimeMs: number) => {
    trackEvent('tool_generation_completed', { tool_type: toolType, video_id: videoId, generation_time_ms: generationTimeMs })
  },

  resultCopied: (toolType: string, videoId?: string) => {
    trackEvent('tool_result_copied', { tool_type: toolType, video_id: videoId })
  },

  clicked: (toolType: string, source: string) => {
    trackEvent('tool_clicked', { tool_type: toolType, source })
  },
}

/**
 * Clip Tool Analytics
 * Track clip tool page views, detection events, and export interactions
 */
export const ClipToolAnalytics = {
  pageViewed: (toolSlug: string, toolCategory: string) => {
    trackEvent('clip_tool_page_viewed', {
      tool_slug: toolSlug,
      tool_category: toolCategory,
      referrer: typeof window !== 'undefined' ? document.referrer : undefined,
    })
  },

  urlPasted: (toolSlug: string, videoDuration?: number) => {
    trackEvent('clip_tool_url_pasted', {
      tool_slug: toolSlug,
      video_duration: videoDuration,
    })
  },

  detectionStarted: (toolSlug: string, videoDuration?: number, isSignedIn?: boolean) => {
    trackEvent('clip_detection_started', {
      tool_slug: toolSlug,
      video_duration: videoDuration,
      is_signed_in: isSignedIn,
    })
  },

  detectionCompleted: (toolSlug: string, clipsFound: number, avgViralityScore?: number, videoDuration?: number) => {
    trackEvent('clip_detection_completed', {
      tool_slug: toolSlug,
      clips_found: clipsFound,
      avg_virality_score: avgViralityScore,
      video_duration: videoDuration,
    })
  },

  /**
   * Track an export entry point being clicked. `source` names the surface
   * ('tool_page', 'editor', 'library', ...) so the authenticated entry points
   * are comparable with the public tool page, which is the only surface that
   * carries a tool slug.
   */
  exportClicked: (
    source: string,
    options?: { toolSlug?: string; clipViralityScore?: number }
  ) => {
    trackEvent('clip_export_clicked', {
      source,
      tool_slug: options?.toolSlug,
      clip_virality_score: options?.clipViralityScore,
    })
  },
}

/**
 * Track when pricing page is viewed
 */
export const trackPricingViewed = () => {
  trackEvent('pricing_viewed')
}

/**
 * Track when checkout is started
 */
export const trackCheckoutStarted = (planId: string) => {
  trackEvent('checkout_started', { plan_id: planId })
}

/**
 * Track dashboard page views
 */
export const trackDashboardView = (page: string) => {
  trackEvent('dashboard_viewed', { page })
}

/**
 * Track user signup
 */
export const trackSignup = () => {
  trackEvent('user_signed_up')
}
